/**
 * useAdminUsers — the internal staff who may be an organisation's account manager.
 *
 * Sourced from the `Admins` Cognito group via GET /admin/users?group=Admins —
 * the same group `AuthContext` gates this whole app on, so "can sign in here"
 * and "can be an account manager" are the same set by construction.
 *
 * The account manager is OUR person who looks after the client. It is NOT the
 * org's owner/payer (`ownerUserId`/`ownerEmail`/`ownerName`), who is someone
 * inside the client's own organisation — a distinct field, deliberately left
 * alone by this picker.
 *
 * Fetched once per mount; the create dialog and the detail page's edit dialog
 * each mount their own copy, which is cheap (one call, ≤60 users, no usage).
 */

import { useEffect, useState } from 'react';
import { adminApi, type User } from '../services/api';

/** The Cognito group that grants access to this admin app (see AuthContext). */
export const ADMIN_GROUP_NAME = 'Admins';

export interface AdminUserOption {
  userId: string;
  email: string;
  name: string;
}

/**
 * "Firstname Lastname" when Cognito has them, else the email's local part.
 * Never returns an empty string for a user that has an email, so an option is
 * always labelled with something a human recognises.
 */
export function adminUserDisplayName(
  user: Pick<User, 'email' | 'firstName' | 'lastName'>,
): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  const email = user.email || '';
  return email.split('@')[0] || email;
}

/** Sorted by display name so the dropdown order is stable between renders. */
export function toAdminUserOptions(users: User[]): AdminUserOption[] {
  return users
    .filter((u) => !!u.email)
    .map((u) => ({
      userId: u.userId,
      email: u.email,
      name: adminUserDisplayName(u),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface UseAdminUsersResult {
  admins: AdminUserOption[];
  loading: boolean;
  error: string | null;
}

export function useAdminUsers(): UseAdminUsersResult {
  const [admins, setAdmins] = useState<AdminUserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await adminApi.listUsers({
          group: ADMIN_GROUP_NAME,
          includeUsage: false, // we only need identity — skip the per-user usage reads
          // Cognito's ListUsersInGroup page ceiling. There are ~9 admins today,
          // so one page covers it — but this does NOT paginate: if the group
          // ever passes 60, page 2 would be silently missing from the dropdown
          // and a NextToken loop becomes necessary.
          limit: 60,
        });
        if (cancelled) return;
        setAdmins(toAdminUserOptions(response?.data?.users || []));
      } catch (err: any) {
        if (cancelled) return;
        setAdmins([]);
        setError(err?.response?.data?.message || err?.message || 'Failed to load admin users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { admins, loading, error };
}
